import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Eye, EyeOff } from "lucide-react";

export default function LoginPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="w-full lg:w-[480px] flex items-center justify-center p-8 bg-white relative">
        <div className="absolute top-4 right-4"><LanguageSwitcher compact /></div>
        <div className="w-full max-w-sm">
          <div className="mb-10">
            <h1 className="text-3xl font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif', color: '#002FA7' }}>LeadVisits</h1>
            <p className="text-sm text-[#868E96] mt-1">{t("auth.sign_in_subtitle")}</p>
          </div>
          {error && <div className="mb-4 p-3 bg-red-50 border-l-4 border-[#C92A2A] text-sm text-[#C92A2A]" data-testid="login-error">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#868E96] block mb-1.5">{t("auth.email")}</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full h-10 px-3 border border-border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent rounded-sm" placeholder="you@agency.com" required data-testid="login-email-input" />
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#868E96] block mb-1.5">{t("auth.password")}</label>
              <div className="relative">
                <input type={showPass ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} className="w-full h-10 px-3 pr-10 border border-border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent rounded-sm" required data-testid="login-password-input" />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#868E96]">{showPass ? <EyeOff size={16} /> : <Eye size={16} />}</button>
              </div>
            </div>
            <button type="submit" disabled={loading} className="w-full h-10 bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#002277] disabled:opacity-50 transition-colors rounded-sm" data-testid="login-submit-button">
              {loading ? t("auth.signing_in") : t("auth.sign_in")}
            </button>
          </form>
          <p className="mt-6 text-sm text-center text-[#868E96]">{t("auth.no_account")} <Link to="/register" className="text-[#002FA7] font-medium hover:underline" data-testid="go-to-register">{t("auth.register")}</Link></p>
        </div>
      </div>
      <div className="hidden lg:flex flex-1 items-center justify-center bg-[#002FA7] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1714601344981-75e003bc5d18?w=1200)', backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="relative z-10 text-white text-center px-12 max-w-lg">
          <h2 className="text-4xl font-black tracking-tight mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>{t("auth.hero_title")}</h2>
          <p className="text-base opacity-80 leading-relaxed">{t("auth.hero_subtitle")}</p>
        </div>
      </div>
    </div>
  );
}
